/* eslint-disable react/no-unstable-nested-components -- DataTable `cell` callbacks are called as plain functions, never mounted as components. */
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router';
import { api, isApiError } from '../../api';
import type { CustomerUserDetail } from '../../api/areas/accounts';
import { Badge } from '../../components/Badge';
import { Callout, DataTable, Section, type Column } from '../../components/blocks';
import { DataError } from '../../components/DataError';
import { EntityRef } from '../../components/EntityRef';
import { KeyValue } from '../../components/KeyValue';
import { PageHeader, useDocumentTitle } from '../../components/PageHeader';
import { CopyText, EmptyState, Loading } from '../../components/States';
import { formatCount, formatDate, formatDateTime, formatRelative } from '../../lib/format';
import { useApi } from '../../lib/useApi';
import { CUSTOMER_STATUS, EnumBadge, EnumText, Masked, mutedSmall, paths, Stack, useEnumLabel, type ToneMap } from './shared';

type Account = CustomerUserDetail['accounts'][number];
type Role = CustomerUserDetail['roles'][number];

const USER_STATUS: ToneMap = { ACTIVE: 'ok', INVITED: 'pending', SUSPENDED: 'serious', DISABLED: 'neutral', LOCKED: 'crit' };

/** `/customers/:id/users/:userId`: one person who signs in for a customer account. E-mail only as a masked hint. */
export function CustomerUserPage() {
  const { t } = useTranslation('accounts');
  const label = useEnumLabel('accounts');
  const { id = '', userId = '' } = useParams();
  const result = useApi((signal) => api.getCustomerUser(id, userId, { signal }), [id, userId]);
  const name = result.status === 'success' ? result.data.user.display_name : t('user.fallbackTitle');
  const customerName = result.status === 'success' ? result.data.customer_name : t('customer.fallbackTitle');
  useDocumentTitle(name);

  let body;
  if (result.status === 'loading') body = <Loading label={t('user.loading')} rows={4} />;
  else if (result.status === 'error') {
    const notFound = isApiError(result.error) && result.error.status === 404;
    body = <DataError error={result.error} onRetry={notFound ? undefined : result.reload} title={notFound ? t('user.notFound') : t('user.loadError')} />;
  } else {
    const d = result.data;
    const u = d.user;
    let mfa: string = t('user.mfa.optional');
    if (u.mfa_enrolled) mfa = t('user.mfa.enrolled');
    else if (u.mfa_required) mfa = t('user.mfa.required');
    const accountColumns: Column<Account>[] = [
      {
        key: 'customer',
        header: t('user.accountCol.customer'),
        cell: (a) => <EntityRef to={paths.customer(a.customer_id)} id={a.customer_id} label={a.customer_name} sub={a.customer_id === d.customer_id ? t('user.thisAccount') : undefined} className="min-w-40" />,
      },
      { key: 'type', header: t('user.accountCol.type'), cell: (a) => <EnumText group="accountType" value={a.account_type} /> },
      { key: 'status', header: t('user.accountCol.status'), cell: (a) => <EnumBadge group="customerStatus" tones={CUSTOMER_STATUS} value={a.status} /> },
      { key: 'relation', header: t('user.accountCol.relation'), cell: (a) => <EnumText group="relation" value={a.relation} /> },
      { key: 'linked', header: t('user.accountCol.linked'), className: 'whitespace-nowrap', cell: (a) => formatDate(a.linked_at) },
    ];
    const roleColumns: Column<Role>[] = [
      {
        key: 'role',
        header: t('user.roleCol.role'),
        cell: (r) => (
          <span className="block min-w-36" title={r.role_code}>
            <span className="block font-medium">{label('role', r.role_code)}</span>
          </span>
        ),
      },
      {
        key: 'scope',
        header: t('user.roleCol.scope'),
        cell: (r) => {
          if (r.scope_type === 'GLOBAL') return t('user.scopeGlobal');
          if (!r.scope_id) return <EntityRef label={t('user.scopeHidden')} sub={label('scopeType', r.scope_type)} className="min-w-36 text-muted-foreground" />;
          return <EntityRef to={paths.customer(r.scope_id)} id={r.scope_id} label={r.scope_name ?? undefined} sub={label('scopeType', r.scope_type)} className="min-w-36" />;
        },
      },
      { key: 'granted', header: t('user.roleCol.granted'), className: 'whitespace-nowrap', cell: (r) => formatDate(r.granted_at) },
      { key: 'grantedBy', header: t('user.roleCol.grantedBy'), cell: (r) => r.granted_by_name ?? <span className="text-muted-foreground">—</span> },
      {
        key: 'revoked',
        header: t('user.roleCol.revoked'),
        className: 'whitespace-nowrap',
        cell: (r) =>
          r.revoked_at ? (
            <Badge tone="neutral" icon={false}>
              {t('user.revokedOn', { date: formatDate(r.revoked_at) })}
            </Badge>
          ) : (
            <Badge tone="ok" icon={false}>
              {t('user.active')}
            </Badge>
          ),
      },
    ];
    body = (
      <Stack>
        <Section title={t('user.profile')}>
          <div className="space-y-4">
            <KeyValue
              columns={3}
              rows={[
                [t('user.field.account'), <EntityRef key="c" to={paths.customer(d.customer_id)} id={d.customer_id} label={d.customer_name} />],
                [t('user.field.relation'), <EnumText key="r" group="relation" value={d.relation} />],
                [t('user.field.linked'), formatDateTime(d.linked_at)],
                [t('user.field.email'), <Masked key="e" value={u.email_hint} tagged />],
                [t('user.field.kind'), <EnumText key="k" group="userKind" value={u.user_kind} />],
                [t('common.status'), <EnumBadge key="s" group="userStatus" tones={USER_STATUS} value={u.status} />],
                [t('user.field.mfa'), mfa],
                [t('user.field.locked'), u.locked ? t('common.yes') : t('common.no')],
                [
                  t('user.field.lastLogin'),
                  d.last_login_at ? (
                    <span key="l" title={formatDateTime(d.last_login_at)}>
                      {formatRelative(d.last_login_at)}
                    </span>
                  ) : (
                    t('common.never')
                  ),
                ],
                [t('user.field.sessions'), formatCount(d.active_sessions)],
                [t('common.created'), formatDateTime(u.created_at)],
                [t('common.id'), <CopyText key="id" value={u.id} />],
              ]}
            />
            <Callout tone="info">{t('user.maskedNote')}</Callout>
          </div>
        </Section>
        <Section flush title={t('user.accounts', { count: d.accounts.length })}>
          <DataTable columns={accountColumns} rows={d.accounts} rowKey={(a) => a.customer_id} />
        </Section>
        <Section flush title={t('user.roles', { count: d.roles.length })}>
          <DataTable columns={roleColumns} rows={d.roles} rowKey={(r) => `${r.role_code}/${r.scope_type}/${r.scope_id ?? ''}/${r.granted_at}`} empty={<EmptyState title={t('user.noRoles')} />} />
        </Section>
      </Stack>
    );
  }

  return (
    <>
      <PageHeader
        title={name}
        subtitle={result.status === 'success' ? <span className={mutedSmall}>{t('user.subtitle', { account: result.data.customer_name })}</span> : undefined}
        crumbs={[{ label: t('crumb') }, { label: t('customers.title'), to: '/customers' }, { label: customerName, to: paths.customer(id) }, { label: name }]}
      />
      {body}
    </>
  );
}
